/**
 * DPIC Adapter - Intercepts dreamfit.newportai.com API calls and redirects to Shopify native flow.
 *
 * Intercepts 3 API endpoints:
 * 1. GET  /product/info       → Returns config from window.__DPIC_PURCHASE_CONFIG__
 * 2. POST /file/upload         → Uploads to Cloudinary instead
 * 3. POST /product/cart/create → Adds to Shopify cart via /cart/add.js, returns /checkout URL
 *
 * Must be loaded BEFORE dpic-purchase-app.min.js
 */
(function() {
  'use strict';

  var CONFIG = window.__DPIC_PURCHASE_CONFIG__;
  if (!CONFIG) {
    console.error('[DPIC Adapter] __DPIC_PURCHASE_CONFIG__ not found! Adapter will not work.');
    return;
  }

  var DREAMFIT_HOST = 'dreamfit.newportai.com';

  // Store original XHR methods
  var origOpen = XMLHttpRequest.prototype.open;
  var origSend = XMLHttpRequest.prototype.send;
  var origSetRequestHeader = XMLHttpRequest.prototype.setRequestHeader;

  XMLHttpRequest.prototype.open = function(method, url, async, user, password) {
    this.__dpic_method = (method || 'GET').toUpperCase();
    this.__dpic_url = url || '';
    this.__dpic_headers = {};
    return origOpen.apply(this, arguments);
  };

  XMLHttpRequest.prototype.setRequestHeader = function(name, value) {
    if (this.__dpic_headers) {
      this.__dpic_headers[name] = value;
    }
    return origSetRequestHeader.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function(body) {
    var url = this.__dpic_url || '';
    var self = this;

    if (url.indexOf(DREAMFIT_HOST) !== -1) {
      handleDpicRequest(self, self.__dpic_method || 'GET', url, body);
      return;
    }

    return origSend.apply(this, arguments);
  };

  function simulateResponse(xhr, data, httpStatus) {
    httpStatus = httpStatus || 200;
    var responseText = JSON.stringify({
      status_code: 0,
      status_msg: 'Success',
      data: data
    });

    setXhrProps(xhr, {
      readyState: 4,
      status: httpStatus,
      statusText: httpStatus === 200 ? 'OK' : 'Error',
      responseText: responseText,
      response: responseText,
      responseURL: xhr.__dpic_url,
      responseXML: null,
      responseType: ''
    });

    fireXhrEvents(xhr, false);
  }

  function simulateError(xhr, message) {
    var responseText = JSON.stringify({
      status_code: -1,
      status_msg: message,
      data: null
    });

    setXhrProps(xhr, {
      readyState: 4,
      status: 500,
      statusText: 'Internal Server Error',
      responseText: responseText,
      response: responseText,
      responseType: ''
    });

    fireXhrEvents(xhr, true);
  }

  function setXhrProps(xhr, props) {
    for (var key in props) {
      if (props.hasOwnProperty(key)) {
        try {
          Object.defineProperty(xhr, key, {
            value: props[key],
            writable: true,
            configurable: true
          });
        } catch(e) {
          xhr[key] = props[key];
        }
      }
    }
  }

  function fireXhrEvents(xhr, isError) {
    // readyState change
    if (typeof xhr.onreadystatechange === 'function') {
      try { xhr.onreadystatechange(); } catch(e) {}
    }
    try { xhr.dispatchEvent(new Event('readystatechange')); } catch(e) {}

    if (isError) {
      try { xhr.dispatchEvent(new Event('error')); } catch(e) {}
      if (typeof xhr.onerror === 'function') {
        try { xhr.onerror(); } catch(e) {}
      }
    } else {
      try { xhr.dispatchEvent(new Event('load')); } catch(e) {}
      if (typeof xhr.onload === 'function') {
        try { xhr.onload(); } catch(e) {}
      }
    }

    try { xhr.dispatchEvent(new Event('loadend')); } catch(e) {}
    if (typeof xhr.onloadend === 'function') {
      try { xhr.onloadend(); } catch(e) {}
    }
  }

  function handleDpicRequest(xhr, method, url, body) {
    try {
      if (url.indexOf('/product/info') !== -1) {
        // Synchronous response
        var data = handleProductInfo(url);
        // Use setTimeout to ensure async behavior (axios expects async)
        setTimeout(function() { simulateResponse(xhr, data); }, 0);
      } else if (url.indexOf('/file/upload') !== -1) {
        handleFileUpload(body).then(function(data) {
          simulateResponse(xhr, data);
        }).catch(function(err) {
          simulateError(xhr, err.message || 'Upload failed');
        });
      } else if (url.indexOf('/product/cart/create') !== -1) {
        handleCartCreate(body).then(function(data) {
          simulateResponse(xhr, data);
        }).catch(function(err) {
          simulateError(xhr, err.message || 'Cart creation failed');
        });
      } else {
        // Unknown endpoint - pass through
        return origSend.call(xhr, body);
      }
    } catch(err) {
      setTimeout(function() { simulateError(xhr, err.message || 'Adapter error'); }, 0);
    }
  }

  function handleProductInfo(url) {
    var productTitle = 'For_Father';
    try {
      var urlObj = new URL(url, window.location.origin);
      productTitle = urlObj.searchParams.get('product_title') || 'For_Father';
    } catch(e) {
      // Fallback: parse query string manually
      var qIndex = url.indexOf('?');
      if (qIndex !== -1) {
        var params = url.substring(qIndex + 1).split('&');
        for (var i = 0; i < params.length; i++) {
          var pair = params[i].split('=');
          if (pair[0] === 'product_title') {
            productTitle = decodeURIComponent(pair[1]);
            break;
          }
        }
      }
    }

    var info = CONFIG.productInfo[productTitle] || CONFIG.productInfo['For_Father'];
    if (!info) {
      throw new Error('Product info not found for: ' + productTitle);
    }
    return info;
  }

  function handleFileUpload(body) {
    return new Promise(function(resolve, reject) {
      var file = null;
      try {
        if (body instanceof FormData) {
          file = body.get('file');
        }
      } catch(e) {
        // Try alternative methods
        if (body && body.get) {
          file = body.get('file');
        }
      }

      if (!file) {
        reject(new Error('No file found in upload request'));
        return;
      }

      var cloudName = CONFIG.cloudinary.cloudName;
      var preset = CONFIG.cloudinary.preset;

      if (!cloudName || cloudName.indexOf('__') === 0) {
        reject(new Error('Cloudinary not configured. Please set cloud name and upload preset.'));
        return;
      }

      var formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', preset);

      fetch('https://api.cloudinary.com/v1_1/' + cloudName + '/image/upload', {
        method: 'POST',
        body: formData
      }).then(function(resp) {
        if (!resp.ok) {
          return resp.text().then(function(text) {
            throw new Error('Cloudinary upload failed (' + resp.status + '): ' + text.substring(0, 200));
          });
        }
        return resp.json();
      }).then(function(result) {
        if (result.secure_url) {
          resolve(result.secure_url);
        } else {
          reject(new Error('Cloudinary did not return a valid URL'));
        }
      }).catch(function(err) {
        reject(err);
      });
    });
  }

  function handleCartCreate(body) {
    return new Promise(function(resolve, reject) {
      var parsed;
      try {
        parsed = typeof body === 'string' ? JSON.parse(body) : body;
      } catch(e) {
        reject(new Error('Invalid cart request body: ' + e.message));
        return;
      }

      var lines = parsed.lines || [];
      if (lines.length === 0) {
        reject(new Error('No items in cart'));
        return;
      }

      // Convert dreamfit line items to Shopify cart/add.js format
      var items = [];
      for (var i = 0; i < lines.length; i++) {
        var line = lines[i];
        var variantId = line.merchandiseId || line.id;
        if (variantId) {
          variantId = String(variantId).replace('gid://shopify/ProductVariant/', '');
        }

        if (!variantId || variantId === 'undefined' || variantId === 'null') {
          console.warn('[DPIC Adapter] Skipping line item without valid variantId:', line);
          continue;
        }

        var item = {
          id: parseInt(variantId, 10),
          quantity: line.quantity || 1
        };

        // Convert attributes to Shopify line item properties
        if (line.attributes && line.attributes.length > 0) {
          item.properties = {};
          for (var j = 0; j < line.attributes.length; j++) {
            var attr = line.attributes[j];
            // Skip properties starting with underscore (internal)
            if (attr.key && attr.key.charAt(0) !== '_') {
              item.properties[attr.key] = attr.value;
            } else {
              item.properties[attr.key] = attr.value;
            }
          }
        }

        items.push(item);
      }

      if (items.length === 0) {
        reject(new Error('No valid items to add to cart'));
        return;
      }

      console.log('[DPIC Adapter] Adding to cart:', items);

      // Add to Shopify cart
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items })
      }).then(function(resp) {
        if (!resp.ok) {
          return resp.text().then(function(text) {
            throw new Error('Cart add failed (' + resp.status + '): ' + text.substring(0, 300));
          });
        }
        return resp.json();
      }).then(function() {
        // Return checkout URL for redirect
        resolve({
          checkOutUrl: '/checkout',
          cartId: 'local-' + Date.now()
        });
      }).catch(function(err) {
        reject(err);
      });
    });
  }

  console.log('[DPIC Adapter] Initialized - intercepting dreamfit.newportai.com API calls');
})();


/* FADELESS SUBJECT BUTTONS V1 */
(function () {
  'use strict';

  if (window.location.pathname !== '/pages/order') return;

  var GOLD = '#8a6a32';

  function installSubjectButtons() {
    var section = document.querySelector('.dp-upload-subjects');
    if (!section || section.querySelector('.fadeless-subject-buttons')) return;

    var stepper = section.querySelector('.dp-upload-subjects__stepper');
    var input = section.querySelector('.dp-upload-subjects__input');
    if (!stepper || !input) return;

    var grid = document.createElement('div');
    grid.className = 'fadeless-subject-buttons';
    grid.setAttribute('role', 'group');
    grid.setAttribute('aria-label', 'Number of subjects');

    function currentCount() {
      return parseInt(input.value, 10) || 0;
    }

    function updateSelected() {
      var selected = currentCount();
      var buttons = grid.querySelectorAll('button');
      for (var i = 0; i < buttons.length; i++) {
        var active = parseInt(buttons[i].getAttribute('data-subject-count'), 10) === selected;
        buttons[i].classList.toggle('is-selected', active);
        buttons[i].setAttribute('aria-pressed', active ? 'true' : 'false');
      }
    }

    function setCount(count) {
      var nativeSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set;
      nativeSetter.call(input, String(count));
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
      window.requestAnimationFrame(updateSelected);
    }

    for (var count = 1; count <= 12; count++) {
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'fadeless-subject-button';
      button.textContent = String(count);
      button.setAttribute('data-subject-count', String(count));
      button.setAttribute('aria-label', count + (count === 1 ? ' subject' : ' subjects'));
      button.setAttribute('aria-pressed', 'false');
      button.addEventListener('click', (function (value) {
        return function () { setCount(value); };
      })(count));
      grid.appendChild(button);
    }

    stepper.hidden = true;
    stepper.setAttribute('aria-hidden', 'true');
    stepper.insertAdjacentElement('afterend', grid);
    input.addEventListener('input', updateSelected);
    input.addEventListener('change', updateSelected);

    if (currentCount() < 1 || currentCount() > 12) setCount(1);
    else updateSelected();
  }

  function addStyles() {
    if (document.getElementById('fadeless-subject-buttons-style')) return;
    var style = document.createElement('style');
    style.id = 'fadeless-subject-buttons-style';
    style.textContent =
      '.dp-upload-subjects__row{align-items:flex-end!important;gap:18px!important}' +
      '.fadeless-subject-buttons{display:grid;grid-template-columns:repeat(12,minmax(34px,1fr));gap:7px;flex:1;max-width:610px}' +
      '.fadeless-subject-button{appearance:none;-webkit-appearance:none;min-width:34px;height:40px;padding:0;border:1px solid ' + GOLD + ';border-radius:6px;background:#fff;color:#3b342b;font:500 15px/1 inherit;cursor:pointer;transition:background-color .18s ease,color .18s ease,box-shadow .18s ease,transform .18s ease}' +
      '.fadeless-subject-button:hover{background:#f7f2e9;box-shadow:0 2px 8px rgba(79,58,25,.12)}' +
      '.fadeless-subject-button:focus-visible{outline:2px solid ' + GOLD + ';outline-offset:2px}' +
      '.fadeless-subject-button.is-selected{background:' + GOLD + ';border-color:' + GOLD + ';color:#fff;box-shadow:0 2px 8px rgba(79,58,25,.2)}' +
      '@media(max-width:749px){.dp-upload-subjects__row{display:block!important}.fadeless-subject-buttons{grid-template-columns:repeat(6,1fr);gap:8px;margin-top:16px;max-width:none}.fadeless-subject-button{width:100%;height:40px}}';
    document.head.appendChild(style);
  }

  addStyles();
  installSubjectButtons();
  new MutationObserver(installSubjectButtons).observe(document.documentElement, { childList: true, subtree: true });
})();


/* FADELESS SUBJECT LAYOUT V2 */
(function () {
  'use strict';

  if (window.location.pathname !== '/pages/order') return;

  function installSubjectLayoutV2() {
    var section = document.querySelector('.dp-upload-subjects');
    if (!section) return;

    var title = section.querySelector('.dp-upload-subjects__title');
    var row = section.querySelector('.dp-upload-subjects__row');
    var priceRow = section.querySelector('.dp-upload-subjects__price-row');
    var grid = section.querySelector('.fadeless-subject-buttons');
    if (!title || !row || !priceRow || !grid) return;

    if (title.textContent.trim() !== 'Figures in painting (People/Pet)') {
      title.textContent = 'Figures in painting (People/Pet)';
    }
    if (title.nextElementSibling !== priceRow) {
      title.insertAdjacentElement('afterend', priceRow);
    }
    if (row.firstElementChild !== grid) {
      row.insertBefore(grid, row.firstElementChild);
    }
    section.classList.add('fadeless-subject-layout-v2');
  }

  function addSubjectLayoutStylesV2() {
    if (document.getElementById('fadeless-subject-layout-v2-style')) return;
    var style = document.createElement('style');
    style.id = 'fadeless-subject-layout-v2-style';
    style.textContent =
      '.fadeless-subject-layout-v2 .dp-upload-subjects__title{margin:0 0 5px!important;font-size:24px!important;line-height:1.25!important}' +
      '.fadeless-subject-layout-v2 .dp-upload-subjects__price-row{margin:0 0 14px!important;line-height:1.2!important}' +
      '.fadeless-subject-layout-v2 .dp-upload-subjects__row{display:block!important;margin:0!important}' +
      '.fadeless-subject-layout-v2 .dp-upload-subjects__left,.fadeless-subject-layout-v2 .dp-upload-subjects__desc{display:none!important}' +
      '.fadeless-subject-layout-v2 .fadeless-subject-buttons{margin:0!important;max-width:610px!important}' +
      '@media(max-width:749px){.fadeless-subject-layout-v2 .dp-upload-subjects__title{font-size:20px!important}.fadeless-subject-layout-v2 .dp-upload-subjects__price-row{margin-bottom:12px!important}.fadeless-subject-layout-v2 .fadeless-subject-buttons{margin:0!important;max-width:none!important}}';
    document.head.appendChild(style);
  }

  addSubjectLayoutStylesV2();
  installSubjectLayoutV2();
  new MutationObserver(installSubjectLayoutV2).observe(document.documentElement, { childList: true, subtree: true });
})();


/* Fadeless Background (House/Landscape) selector */
(function () {
  'use strict';
  if (window.location.pathname !== '/pages/order') return;
  var GOLD = '#9a742f';
  window.__FADELESS_BACKGROUND__ = 0;

  function installBackgroundSelector() {
    var section = document.querySelector('.dp-upload-subjects');
    var subjectGrid = section && section.querySelector('.fadeless-subject-buttons');
    if (!section || !subjectGrid || section.querySelector('.fadeless-background-selector')) return;
    var group = document.createElement('div');
    group.className = 'fadeless-background-selector';
    var title = document.createElement('h3');
    title.className = 'fadeless-background-selector__title';
    title.textContent = 'Background (House/Landscape)';
    var buttons = document.createElement('div');
    buttons.className = 'fadeless-background-selector__buttons';
    [0, 1].forEach(function (value) {
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'fadeless-subject-button fadeless-background-button' + (value === 0 ? ' is-selected' : '');
      button.textContent = value === 0 ? 'No' : 'Yes';
      button.dataset.backgroundValue = String(value);
      button.setAttribute('aria-label', value ? 'Include house or landscape background' : 'No custom background');
      button.setAttribute('aria-pressed', value === 0 ? 'true' : 'false');
      button.addEventListener('click', function () {
        window.__FADELESS_BACKGROUND__ = value;
        price.textContent = value === 1 ? '$50.00' : '$0.00';
        group.querySelectorAll('.fadeless-background-button').forEach(function (item) {
          var active = Number(item.dataset.backgroundValue) === value;
          item.classList.toggle('is-selected', active);
          item.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
      });
      buttons.appendChild(button);
    });
    group.appendChild(title);
    var priceRow = document.createElement('div');
    priceRow.className = 'dp-upload-subjects__price-row fadeless-background-selector__price-row';
    var price = document.createElement('span');
    price.className = 'dp-upload-subjects__price fadeless-background-selector__price';
    price.textContent = '$0.00';
    priceRow.appendChild(price);
    group.appendChild(priceRow);
    group.appendChild(buttons);
    subjectGrid.parentElement.insertAdjacentElement('afterend', group);
  }

  if (!window.__FADELESS_BACKGROUND_FETCH_PATCHED__) {
    window.__FADELESS_BACKGROUND_FETCH_PATCHED__ = true;
    var originalFetch = window.fetch;
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      if (/\/cart\/add\.js(?:\?|$)/.test(url) && init && typeof init.body === 'string') {
        try {
          var payload = JSON.parse(init.body);
          if (payload.items && payload.items[0]) {
            payload.items[0].properties = payload.items[0].properties || {};
            payload.items[0].properties.Background = window.__FADELESS_BACKGROUND__ === 1 ? 'House/Landscape' : 'None';
            if (window.__FADELESS_BACKGROUND__ === 1 && !payload.items.some(function (item) { return Number(item.id) === 48970174955742; })) {
              var sourceProperties = payload.items[0].properties || {};
              var backgroundProperties = { '_Customization': 'Background (House/Landscape)', 'Background': 'Yes' };
              ['_Bundle ID', '_Bundle Summary', '_Bundle Image'].forEach(function (key) { if (sourceProperties[key]) backgroundProperties[key] = sourceProperties[key]; });
              backgroundProperties['_Bundle Position'] = String(payload.items.length + 1);
              payload.items.push({ id: 48970174955742, quantity: payload.items[0].quantity || 1, properties: backgroundProperties });
            }
            init = Object.assign({}, init, { body: JSON.stringify(payload) });
          }
        } catch (error) { console.warn('[Fadeless] Background cart property skipped', error); }
      }
      return originalFetch.call(this, input, init);
    };
  }

  function addStyles() {
    if (document.getElementById('fadeless-background-selector-style-v6')) return;
    var style = document.createElement('style');
    style.id = 'fadeless-background-selector-style-v6';
    style.textContent =
      '.fadeless-background-selector{margin-top:40px}' +
      '.fadeless-background-selector__title{margin:0 0 12px;font-family:inherit;font-size:24px;line-height:1.25;font-weight:500;letter-spacing:normal}' +
      '.dp-upload-subjects__price:before{content:"+"}' +
      '.fadeless-background-selector__price-row{margin:0 0 14px;line-height:1.2}' +
      '.fadeless-background-selector__buttons{display:flex;gap:8px}' +
      '.fadeless-background-button{width:44px;min-width:44px}' +
      '@media(max-width:749px){.fadeless-background-selector{margin-top:40px}.fadeless-background-selector__title{font-size:24px}}';
    document.head.appendChild(style);
  }
  addStyles();
  installBackgroundSelector();
  new MutationObserver(installBackgroundSelector).observe(document.documentElement, { childList: true, subtree: true });
})();


/* Fadeless Background fee: typography, live summary total and detail row */
(function () {
  'use strict';
  if (window.location.pathname !== '/pages/order') return;
  var FEE = 50;
  var scheduled = false;

  function moneyNumber(text) {
    var value = parseFloat(String(text || '').replace(/[^0-9.-]/g, ''));
    return Number.isFinite(value) ? value : 0;
  }
  function currencyPrefix(text) {
    var match = String(text || '').match(/^[^0-9-]*/);
    return match && match[0].replace('+', '') || '$';
  }
  function adjustTotal(element, active) {
    if (!element) return;
    var current = moneyNumber(element.textContent);
    var applied = element.dataset.fadelessBackgroundFee === '1';
    var base = moneyNumber(element.dataset.fadelessBackgroundBase);
    if (active) {
      if (!applied || Math.abs(current - (base + FEE)) > 0.001) base = current;
      element.dataset.fadelessBackgroundBase = String(base);
      element.dataset.fadelessBackgroundFee = '1';
      var target = currencyPrefix(element.textContent) + (base + FEE).toFixed(2).replace(/.00$/, '');
      if (element.textContent !== target) element.textContent = target;
    } else if (applied) {
      var restored = currencyPrefix(element.textContent) + base.toFixed(2).replace(/.00$/, '');
      element.dataset.fadelessBackgroundFee = '0';
      if (element.textContent !== restored) element.textContent = restored;
    }
  }
  function syncTotals(active) {
    document.querySelectorAll('.dp-summary-bar__price-current, .dp-summary-bar__price-only, .dp-summary-bar__price-original').forEach(function (element) {
      adjustTotal(element, active);
    });
  }
  function syncDetail(active) {
    document.querySelectorAll('.fadeless-background-summary-row').forEach(function (row) {
      if (!active) row.remove();
    });
    if (!active) return;
    document.querySelectorAll('.dp-summary-detail').forEach(function (detail) {
      if (detail.querySelector('.fadeless-background-summary-row')) return;
      var source = detail.querySelector('.dp-summary-detail__row');
      if (!source) return;
      var row = source.cloneNode(true);
      row.classList.add('fadeless-background-summary-row');
      var icon = row.querySelector('.dp-summary-detail__icon');
      var title = row.querySelector('.dp-summary-detail__title');
      var subtitle = row.querySelector('.dp-summary-detail__subtitle');
      var price = row.querySelector('.dp-summary-detail__price');
      var original = row.querySelector('.dp-summary-detail__price-original');
      if (icon) icon.innerHTML = '<span class="fadeless-background-summary-icon">BG</span>';
      if (title) title.textContent = 'Background';
      if (subtitle) subtitle.textContent = 'House/Landscape';
      if (price) price.textContent = '$50.00';
      if (original) original.remove();
      source.insertAdjacentElement('afterend', row);
    });
  }
  function sync() {
    scheduled = false;
    var active = window.__FADELESS_BACKGROUND__ === 1;
    syncTotals(active);
    syncDetail(active);
  }
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(sync);
  }
  function addStyles() {
    if (document.getElementById('fadeless-background-summary-sync-v1')) return;
    var style = document.createElement('style');
    style.id = 'fadeless-background-summary-sync-v1';
    style.textContent =
      '.fadeless-background-selector__price{font-family:inherit!important;font-size:20px!important;font-weight:600!important;line-height:normal!important;color:#403934!important}' +
      '.fadeless-background-summary-icon{font-size:12px;font-weight:600;color:#fffdf8}' +
      '@media(max-width:1024px){.fadeless-background-selector__price{font-size:16px!important}}';
    document.head.appendChild(style);
  }
  addStyles();
  document.addEventListener('click', function (event) {
    if (event.target.closest('.fadeless-background-button, .dp-summary-bar__price')) schedule();
  });
  new MutationObserver(schedule).observe(document.documentElement, { childList: true, subtree: true, characterData: true });
  schedule();
})();


/* Fadeless Size: four-column cards with one selected-price line */
(function () {
  'use strict';
  if (window.location.pathname !== '/pages/order') return;
  var scheduled = false;
  function syncSizePrice() {
    scheduled = false;
    var list = document.querySelector('.dp-options-size-list');
    if (!list) return;
    var section = list.closest('.dp-options-section');
    var title = section && section.querySelector('.dp-options-section__title');
    if (!section || !title) return;
    section.classList.add('fadeless-size-section');
    var row = section.querySelector('.fadeless-size-price-row');
    if (!row) {
      row = document.createElement('div');
      row.className = 'dp-options-size-card__price fadeless-size-price-row';
      title.insertAdjacentElement('afterend', row);
    }
    var active = list.querySelector('.dp-options-size-card--active') || list.querySelector('.dp-options-size-card');
    var price = active && active.querySelector('.dp-options-size-card__price');
    var value = price ? price.textContent.trim().replace(/^\+/, '') : '$0.00';
    var display = '+' + value;
    if (row.textContent !== display) row.textContent = display;
  }
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(syncSizePrice);
  }
  function addStyles() {
    if (document.getElementById('fadeless-size-four-column-v1')) return;
    var style = document.createElement('style');
    style.id = 'fadeless-size-four-column-v1';
    style.textContent =
      '.fadeless-size-section{gap:0!important}' +
      '.fadeless-size-section>.dp-options-section__title{margin:0 0 5px!important}' +
      '.fadeless-size-price-row{margin:0 0 14px!important;font-family:inherit!important;font-size:20px!important;font-weight:600!important;line-height:1.2!important;color:#403934!important}' +
      '.dp-options-size-list{grid-template-columns:repeat(4,minmax(0,1fr))!important;gap:10px!important}' +
      '.dp-options-size-card{min-width:0!important;min-height:82px!important;padding:14px 10px!important;justify-content:center!important;align-items:center!important;text-align:center!important}' +
      '.dp-options-size-card__label{font-size:18px!important;white-space:nowrap!important}' +
      '.dp-options-size-card>.dp-options-size-card__price-row{display:none!important}' +
      '@media(max-width:749px){.fadeless-size-price-row{font-size:16px!important;margin-bottom:12px!important}.dp-options-size-list{gap:6px!important}.dp-options-size-card{min-height:58px!important;padding:8px 3px!important}.dp-options-size-card__label{font-size:13px!important}}';
    document.head.appendChild(style);
  }
  addStyles();
  document.addEventListener('click', function (event) {
    if (event.target.closest('.dp-options-size-card')) schedule();
  });
  new MutationObserver(schedule).observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
  schedule();
})();


/* Fadeless Frame: four-column cards with one selected-price line */
(function () {
  'use strict';
  if (window.location.pathname !== '/pages/order') return;
  var scheduled = false;
  function normalize(value) {
    return String(value || '').toLowerCase()
      .split(' ').join('').split('×').join('').split('x').join('')
      .split('"').join('').split('″').join('').split('”').join('');
  }
  function selectedFramePrice() {
    var sizeCard = document.querySelector('.dp-options-size-card--active');
    var frameCard = document.querySelector('.dp-options-frame-card--active') || document.querySelector('.dp-options-frame-card');
    var sizeLabel = sizeCard && sizeCard.querySelector('.dp-options-size-card__label');
    var size = sizeLabel ? sizeLabel.textContent.trim() : '';
    var frame = frameCard ? (frameCard.getAttribute('aria-label') || '') : '';
    if (!size || !frame) return '$0.00';
    var config = window.__DPIC_PURCHASE_CONFIG__;
    var infos = config && config.productInfo ? Object.keys(config.productInfo).map(function (key) { return config.productInfo[key]; }) : [];
    var wanted = normalize(size + frame);
    for (var i = 0; i < infos.length; i++) {
      var combinations = infos[i] && infos[i].SizeFrame;
      if (!combinations) continue;
      var keys = Object.keys(combinations);
      for (var j = 0; j < keys.length; j++) {
        if (normalize(keys[j]) === wanted) return '$' + Number(combinations[keys[j]].price || 0).toFixed(2);
      }
    }
    return '$0.00';
  }
  function syncFramePrice() {
    scheduled = false;
    var grid = document.querySelector('.dp-options-frame-grid');
    if (!grid) return;
    var section = grid.closest('.dp-options-section');
    var title = section && section.querySelector('.dp-options-section__title');
    if (!section || !title) return;
    section.classList.add('fadeless-frame-section');
    var row = section.querySelector('.fadeless-frame-price-row');
    if (!row) {
      row = document.createElement('div');
      row.className = 'dp-options-size-card__price fadeless-frame-price-row';
      title.insertAdjacentElement('afterend', row);
    }
    var display = '+' + selectedFramePrice();
    if (row.textContent !== display) row.textContent = display;
  }
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(syncFramePrice);
  }
  function addStyles() {
    if (document.getElementById('fadeless-frame-four-column-v1')) return;
    var style = document.createElement('style');
    style.id = 'fadeless-frame-four-column-v1';
    style.textContent =
      '.fadeless-frame-section{gap:0!important}' +
      '.fadeless-frame-section>.dp-options-section__title{margin:0 0 5px!important}' +
      '.fadeless-frame-price-row{margin:0 0 14px!important;font-family:inherit!important;font-size:20px!important;font-weight:600!important;line-height:1.2!important;color:#403934!important}' +
      '.dp-options-frame-grid{grid-template-columns:repeat(4,minmax(0,1fr))!important;gap:10px!important}' +
      '.dp-options-frame-card{min-width:0!important}' +
      '@media(max-width:749px){.fadeless-frame-price-row{font-size:16px!important;margin-bottom:12px!important}.dp-options-frame-grid{gap:6px!important}}';
    document.head.appendChild(style);
  }
  addStyles();
  document.addEventListener('click', function (event) {
    if (event.target.closest('.dp-options-frame-card, .dp-options-size-card')) schedule();
  });
  new MutationObserver(schedule).observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
  schedule();
})();


/* Fadeless Frame: five columns and remove GF141-3920 */
(function () {
  'use strict';
  if (window.location.pathname !== '/pages/order') return;
  if (document.getElementById('fadeless-frame-five-column-v1')) return;
  var style = document.createElement('style');
  style.id = 'fadeless-frame-five-column-v1';
  style.textContent =
    '.dp-options-frame-grid{grid-template-columns:repeat(5,minmax(0,1fr))!important;gap:8px!important}' +
    '.dp-options-frame-card[aria-label="GF141-3920"]{display:none!important}' +
    '@media(max-width:749px){.dp-options-frame-grid{grid-template-columns:repeat(5,minmax(0,1fr))!important;gap:5px!important}}';
  document.head.appendChild(style);
})();


/* Fadeless Upload: compact desktop modules so the footer stays fully visible */
(function () {
  'use strict';
  if (window.location.pathname !== '/pages/order') return;
  if (document.getElementById('fadeless-upload-compact-v1')) return;
  var style = document.createElement('style');
  style.id = 'fadeless-upload-compact-v1';
  style.textContent =
    '@media(min-width:990px){' +
      '.dp-purchase-upload-tab .dp-upload-section{margin-bottom:12px!important}' +
      '.dp-purchase-upload-tab .dp-upload-zone{min-height:150px!important;padding:12px!important;gap:12px!important}' +
      '.dp-purchase-upload-tab .dp-upload-zone__icon{width:28px!important;height:28px!important}' +
      '.dp-purchase-upload-tab .dp-upload-zone__text{gap:5px!important}' +
      '.dp-purchase-upload-tab .dp-upload-zone__title{font-size:24px!important}' +
      '.dp-purchase-upload-tab .dp-upload-zone__hint{font-size:16px!important}' +
      '.dp-purchase-upload-tab .dp-upload-requirements{padding:14px 24px!important;margin-bottom:12px!important}' +
      '.dp-purchase-upload-tab .dp-upload-requirements__title{margin-bottom:8px!important;font-size:18px!important}' +
      '.dp-purchase-upload-tab .dp-upload-requirements__list{padding:3px 0!important;line-height:1.2!important}' +
      '.dp-purchase-upload-tab .dp-upload-requirements__list li+li{margin-top:6px!important}' +
      '.dp-purchase-upload-tab .dp-upload-requirements__icon{width:16px!important;height:16px!important}' +
      '.dp-purchase-upload-tab .dp-upload-privacy__title{margin-bottom:4px!important;font-size:18px!important}' +
      '.dp-purchase-upload-tab .dp-upload-privacy__text{line-height:1.2!important}' +
    '}';
  document.head.appendChild(style);
})();


/* Fadeless Upload: keep completed preview compact on desktop */
(function(){
  'use strict';
  if(window.location.pathname!=='/pages/order'||document.getElementById('fadeless-upload-preview-compact-v2'))return;
  var style=document.createElement('style');
  style.id='fadeless-upload-preview-compact-v2';
  style.textContent='@media(min-width:990px){'+
    '.dp-purchase-upload-tab .dp-upload-preview{gap:10px!important}'+
    '.dp-purchase-upload-tab .dp-upload-preview__thumb--large{width:160px!important;height:160px!important}'+
    '.dp-purchase-upload-tab .dp-upload-btn-change{height:32px!important}'+
    '.dp-purchase-upload-tab .dp-upload-section:has(.dp-upload-preview){margin-bottom:10px!important}'+
  '}';
  document.head.appendChild(style);
})();


/* Fadeless Upload: up to five photos plus buyer requirements */
(function(){
  'use strict';
  if(window.location.pathname!=='/pages/order'||window.__fadelessUploadFiveV1)return;
  window.__fadelessUploadFiveV1=true;
  var state={extraUrls:[],note:'',uploading:0,error:''};window.__fadelessUploadFiveState=state;
  var nativeFetch=window.fetch.bind(window);
  function cloudinary(file){
    var cfg=window.__DPIC_PURCHASE_CONFIG__&&window.__DPIC_PURCHASE_CONFIG__.cloudinary||{};
    var body=new FormData();body.append('file',file);body.append('upload_preset',cfg.preset||'ml_default');
    return nativeFetch('https://api.cloudinary.com/v1_1/'+(cfg.cloudName||'yehgs3f3')+'/image/upload',{method:'POST',body:body}).then(function(r){if(!r.ok)throw new Error('Upload failed');return r.json()}).then(function(data){if(!data.secure_url)throw new Error('Upload failed');return data.secure_url});
  }
  function addCartData(payload){
    function visit(value){
      if(!value||typeof value!=='object')return false;
      if(Array.isArray(value)){
        if(value.length&&value[0]&&value[0].merchandiseId){
          var line=value[0],attrs=Array.isArray(line.attributes)?line.attributes.slice():[];
          state.extraUrls.forEach(function(url,index){attrs.push({key:'Additional photo '+(index+2),value:url})});
          if(state.note.trim())attrs.push({key:'Buyer requirements',value:state.note.trim()});
          line.attributes=attrs;return true;
        }
        for(var i=0;i<value.length;i++)if(visit(value[i]))return true;
      }else{for(var key in value)if(Object.prototype.hasOwnProperty.call(value,key)&&visit(value[key]))return true}
      return false;
    }
    visit(payload);return payload;
  }
  window.fetch=function(input,init){
    try{
      if(init&&typeof init.body==='string'&&(state.extraUrls.length||state.note.trim())){
        var json=JSON.parse(init.body);
        if(JSON.stringify(json).indexOf('merchandiseId')!==-1||Array.isArray(json.items)){addCartData(json);init=Object.assign({},init,{body:JSON.stringify(json)})}
      }
    }catch(e){}
    return nativeFetch(input,init);
  };
  function ensureUI(){
    var root=document.querySelector('.dp-purchase-upload-tab');if(!root)return;
    var input=root.querySelector('.dp-upload-input');if(input)input.setAttribute('multiple','multiple');
    var req=root.querySelector('.dp-upload-requirements__list');
    if(req&&!req.querySelector('.fadeless-five-rule')){
      var li=document.createElement('li');li.className='fadeless-five-rule';
      li.innerHTML='<span class="fadeless-rule-dot">•</span><span>Upload up to 5 photos. For more than 5 photos, please <a href="/pages/contact">contact us</a>.</span>';
      req.insertBefore(li,req.firstChild);
    }
    var privacy=root.querySelector('.dp-upload-privacy');
    if(privacy&&!root.querySelector('.fadeless-buyer-note')){
      var box=document.createElement('section');box.className='fadeless-buyer-note';
      box.innerHTML='<h3>Buyer requirements</h3><textarea maxlength="1000" placeholder="Tell us about any special requests, such as combining multiple photos or customizing the background."></textarea><div class="fadeless-upload-status" aria-live="polite"></div>';
      privacy.parentNode.insertBefore(box,privacy);
      box.querySelector('textarea').value=state.note;
      box.querySelector('textarea').addEventListener('input',function(e){state.note=e.target.value});
    }
    render();
  }
  function render(){
    var root=document.querySelector('.dp-purchase-upload-tab');if(!root)return;
    var section=root.querySelector('.dp-upload-section');if(!section)return;
    var gallery=root.querySelector('.fadeless-extra-gallery');
    if(!gallery){gallery=document.createElement('div');gallery.className='fadeless-extra-gallery';section.appendChild(gallery)}
    var desired=state.extraUrls.map(function(url,i){return '<div class="fadeless-extra-photo"><img src="'+url.replace(/"/g,'&quot;')+'" alt="Additional photo '+(i+2)+'"><span>'+(i+2)+'</span><button type="button" class="fadeless-extra-delete" data-index="'+i+'" aria-label="Remove photo '+(i+2)+'">×</button></div>'}).join('');
    if(gallery.innerHTML!==desired)gallery.innerHTML=desired;
    var status=root.querySelector('.fadeless-upload-status');
    var message=state.uploading?'Uploading '+state.uploading+' additional photo(s)…':state.error;
    if(status&&status.textContent!==message)status.textContent=message;
  }
  document.addEventListener('change',function(event){
    var input=event.target.closest&&event.target.closest('.dp-upload-input');if(!input)return;
    var files=Array.prototype.slice.call(input.files||[]);state.extraUrls=[];state.error='';
    if(files.length>5)state.error='Only the first 5 photos will be uploaded. For more than 5 photos, please contact us.';
    var extras=files.slice(1,5);state.uploading=extras.length;render();
    extras.forEach(function(file){cloudinary(file).then(function(url){state.extraUrls.push(url)}).catch(function(){state.error='One additional photo failed to upload. Please try again.'}).finally(function(){state.uploading--;render()})});
  },true);
  var style=document.createElement('style');style.id='fadeless-upload-five-note-v1';
  style.textContent='.fadeless-five-rule a{color:#9b742f;text-decoration:underline}.fadeless-rule-dot{width:20px;flex:0 0 20px;text-align:center;color:#403934}.fadeless-extra-gallery{display:flex;justify-content:center;gap:8px;margin-top:8px;flex-wrap:wrap}.fadeless-extra-photo{position:relative;width:64px;height:64px;border-radius:4px;overflow:hidden;background:#f1f1f1}.fadeless-extra-photo img{width:100%;height:100%;object-fit:cover}.fadeless-extra-photo span{position:absolute;right:3px;bottom:3px;width:18px;height:18px;border-radius:50%;background:#403934;color:#fff;font-size:11px;display:flex;align-items:center;justify-content:center}.fadeless-buyer-note{margin:0 0 12px}.fadeless-buyer-note h3{margin:0 0 8px;text-align:center;font-size:18px;font-weight:500;color:#403934}.fadeless-buyer-note textarea{display:block;width:100%;min-height:72px;resize:vertical;padding:10px 12px;border:1px solid rgba(64,57,52,.35);border-radius:4px;background:#fffdf8;color:#403934;font:14px/1.4 inherit;box-sizing:border-box}.fadeless-buyer-note textarea:focus{outline:1px solid #9b742f;border-color:#9b742f}.fadeless-upload-status{min-height:18px;margin-top:4px;font-size:12px;color:#9b742f}@media(min-width:990px){.fadeless-buyer-note textarea{min-height:58px}.fadeless-extra-photo{width:52px;height:52px}}';
  document.head.appendChild(style);
  new MutationObserver(ensureUI).observe(document.documentElement,{childList:true,subtree:true});
  ensureUI();
})();


/* Fadeless Upload: scope buyer note to Upload and compact completed layout */
(function(){
  'use strict';
  if(window.location.pathname!=='/pages/order'||window.__fadelessUploadScopeV2)return;
  window.__fadelessUploadScopeV2=true;
  function isUploadStep(){
    var tabs=document.querySelectorAll('.dp-purchase-page__tab--active');
    for(var i=0;i<tabs.length;i++)if((tabs[i].textContent||'').trim()==='Upload')return true;
    return false;
  }
  function sync(){
    var root=document.querySelector('.dp-purchase-upload-tab');if(!root)return;
    var note=root.querySelector('.fadeless-buyer-note');
    if(!isUploadStep()){
      if(note&&note.style.display!=='none')note.style.display='none';
      if(root.classList.contains('fadeless-current-upload'))root.classList.remove('fadeless-current-upload');
      return;
    }
    if(!root.classList.contains('fadeless-current-upload'))root.classList.add('fadeless-current-upload');
    if(note&&note.style.display!=='block')note.style.display='block';
  }
  var style=document.createElement('style');style.id='fadeless-upload-scope-height-v2';
  style.textContent=
    '.dp-purchase-upload-tab:not(.fadeless-current-upload) .fadeless-buyer-note{display:none!important}'+
    '@media(max-width:1024px){'+
      '.fadeless-current-upload .dp-upload-section{margin-bottom:6px!important}'+
      '.fadeless-current-upload .dp-upload-preview{gap:6px!important}'+
      '.fadeless-current-upload .dp-upload-preview__thumb--large{width:120px!important;height:120px!important}'+
      '.fadeless-current-upload .dp-upload-btn-change{height:30px!important}'+
      '.fadeless-current-upload .dp-upload-requirements{padding:8px 16px!important;margin-bottom:6px!important}'+
      '.fadeless-current-upload .dp-upload-requirements__title{margin-bottom:5px!important;font-size:16px!important}'+
      '.fadeless-current-upload .dp-upload-requirements__list{padding:0!important;line-height:1.15!important}'+
      '.fadeless-current-upload .dp-upload-requirements__list li+li{margin-top:3px!important}'+
      '.fadeless-current-upload .fadeless-buyer-note{margin-bottom:6px!important}'+
      '.fadeless-current-upload .fadeless-buyer-note h3{margin-bottom:5px!important;font-size:16px!important}'+
      '.fadeless-current-upload .fadeless-buyer-note textarea{min-height:48px!important;height:48px!important;padding:7px 10px!important;resize:none!important}'+
      '.fadeless-current-upload .fadeless-upload-status{min-height:0!important;margin-top:2px!important}'+
      '.fadeless-current-upload .dp-upload-privacy__title{margin-bottom:2px!important;font-size:16px!important}'+
      '.fadeless-current-upload .dp-upload-privacy__text{font-size:11px!important;line-height:1.1!important}'+
    '}';
  document.head.appendChild(style);
  new MutationObserver(sync).observe(document.documentElement,{childList:true,subtree:true,attributes:true,attributeFilter:['class','style']});
  document.addEventListener('click',function(){requestAnimationFrame(sync)});
  sync();
})();


/* Fadeless Upload: merge requirements and privacy into a concise Note */
(function(){
  'use strict';
  if(window.location.pathname!=='/pages/order'||window.__fadelessMergedNoteV1)return;
  window.__fadelessMergedNoteV1=true;
  var listHtml='<li class="fadeless-five-rule"><span class="fadeless-rule-dot">•</span><span>Upload up to 5 clear JPG/PNG photos. For more than 5, please <a href="/pages/contact">contact us</a>.</span></li>'+
    '<li><span class="fadeless-rule-dot">•</span><span>Use photos you have the right to submit. They will only be used to create your personalized artwork.</span></li>';
  function sync(){
    var root=document.querySelector('.dp-purchase-upload-tab');if(!root)return;
    var title=root.querySelector('.dp-upload-requirements__title');
    if(title&&title.textContent!=='Note')title.textContent='Note';
    var list=root.querySelector('.dp-upload-requirements__list');
    if(list&&list.innerHTML!==listHtml)list.innerHTML=listHtml;
    var privacy=root.querySelector('.dp-upload-privacy');
    if(privacy&&privacy.style.display!=='none')privacy.style.display='none';
  }
  var style=document.createElement('style');style.id='fadeless-merged-note-v1';
  style.textContent='.dp-purchase-upload-tab .dp-upload-privacy{display:none!important}.dp-purchase-upload-tab .dp-upload-requirements__list li{align-items:flex-start!important}.dp-purchase-upload-tab .dp-upload-requirements__list a{color:#9b742f;text-decoration:underline}@media(max-width:1024px){.fadeless-current-upload .dp-upload-requirements{padding:9px 16px!important}.fadeless-current-upload .dp-upload-requirements__list li+li{margin-top:5px!important}}';
  document.head.appendChild(style);
  new MutationObserver(sync).observe(document.documentElement,{childList:true,subtree:true});
  sync();
})();


/* Fadeless Upload: uniform vertical rhythm */
(function(){
  'use strict';
  if(window.location.pathname!=='/pages/order'||document.getElementById('fadeless-upload-uniform-spacing-v1'))return;
  var style=document.createElement('style');style.id='fadeless-upload-uniform-spacing-v1';
  style.textContent=
    '.fadeless-current-upload{display:flex!important;flex-direction:column!important;gap:12px!important}'+
    '.fadeless-current-upload>.dp-upload-section,.fadeless-current-upload>.dp-upload-requirements,.fadeless-current-upload>.fadeless-buyer-note{margin:0!important}'+
    '@media(max-width:1024px){'+
      '.dp-purchase-page__tabs--mobile{padding-bottom:0!important}'+
      '.dp-purchase-page__tab-content:has(.fadeless-current-upload){display:flex!important;flex-direction:column!important;margin-top:12px!important;padding-bottom:104px!important}'+
      '.dp-purchase-page__tab-content:has(.fadeless-current-upload)>.fadeless-current-upload{flex:1 1 auto!important;min-height:0!important}'+
      '.fadeless-current-upload>.fadeless-buyer-note{flex:1 1 auto!important;display:flex!important;flex-direction:column!important;min-height:0!important}'+
      '.fadeless-current-upload>.fadeless-buyer-note textarea{flex:1 1 auto!important;height:auto!important;min-height:48px!important}'+
    '}';
  document.head.appendChild(style);
})();


/* Fadeless Upload: horizontal equal-size photo preview */
(function(){
  'use strict';
  if(window.location.pathname!=='/pages/order'||window.__fadelessHorizontalPreviewV1)return;
  window.__fadelessHorizontalPreviewV1=true;
  function sync(){
    var root=document.querySelector('.fadeless-current-upload');if(!root)return;
    var preview=root.querySelector('.dp-upload-preview:not(.dp-upload-preview--invalid)');
    var gallery=root.querySelector('.fadeless-extra-gallery');
    if(preview&&gallery&&gallery.parentNode!==preview){
      var button=preview.querySelector('.dp-upload-btn-change');
      preview.insertBefore(gallery,button||null);
    }
    if(preview)preview.classList.add('fadeless-horizontal-photo-preview');
  }
  var style=document.createElement('style');style.id='fadeless-horizontal-preview-v1';
  style.textContent=
    '.fadeless-horizontal-photo-preview{display:grid!important;grid-template-columns:auto auto!important;align-items:center!important;justify-content:center!important;column-gap:8px!important;row-gap:8px!important}'+
    '.fadeless-horizontal-photo-preview>.dp-upload-preview__thumb--large,.fadeless-horizontal-photo-preview .fadeless-extra-photo{width:82px!important;height:82px!important;flex:0 0 82px!important}'+
    '.fadeless-horizontal-photo-preview>.fadeless-extra-gallery{display:flex!important;flex-wrap:nowrap!important;gap:8px!important;margin:0!important}'+
    '.fadeless-horizontal-photo-preview>.dp-upload-btn-change{grid-column:1/-1!important;justify-self:center!important}'+
    '@media(max-width:520px){'+
      '.fadeless-horizontal-photo-preview{column-gap:6px!important;row-gap:7px!important}'+
      '.fadeless-horizontal-photo-preview>.dp-upload-preview__thumb--large,.fadeless-horizontal-photo-preview .fadeless-extra-photo{width:58px!important;height:58px!important;flex-basis:58px!important}'+
      '.fadeless-horizontal-photo-preview>.fadeless-extra-gallery{gap:6px!important}'+
    '}';
  document.head.appendChild(style);
  new MutationObserver(sync).observe(document.documentElement,{childList:true,subtree:true});
  sync();
})();


/* Fadeless Upload: fill completed-state bottom gap safely */
(function(){
  'use strict';
  if(window.location.pathname!=='/pages/order'||document.getElementById('fadeless-upload-bottom-gap-v1'))return;
  var style=document.createElement('style');style.id='fadeless-upload-bottom-gap-v1';
  style.textContent='@media(max-width:1024px){'+
    '.fadeless-current-upload:has(.fadeless-horizontal-photo-preview)>.fadeless-buyer-note textarea{height:clamp(72px,calc(100dvh - 528px),174px)!important;min-height:72px!important;flex:none!important}'+
  '}';
  document.head.appendChild(style);
})();


/* Fadeless Upload: clear delete controls for every photo */
(function(){
  'use strict';
  if(window.location.pathname!=='/pages/order'||window.__fadelessDeleteButtonsV1)return;
  window.__fadelessDeleteButtonsV1=true;
  function relabel(gallery){
    var cards=gallery.querySelectorAll('.fadeless-extra-photo');
    for(var i=0;i<cards.length;i++){
      var number=cards[i].querySelector('span');if(number)number.textContent=String(i+2);
      var button=cards[i].querySelector('.fadeless-extra-delete');
      if(button){button.dataset.index=String(i);button.setAttribute('aria-label','Remove photo '+(i+2))}
    }
  }
  document.addEventListener('click',function(event){
    var extra=event.target.closest&&event.target.closest('.fadeless-extra-delete');
    var state=window.__fadelessUploadFiveState;
    if(extra&&state){
      event.preventDefault();event.stopPropagation();
      var index=Number(extra.dataset.index);if(Number.isInteger(index)&&index>=0)state.extraUrls.splice(index,1);
      var gallery=extra.closest('.fadeless-extra-gallery');extra.closest('.fadeless-extra-photo').remove();if(gallery)relabel(gallery);return;
    }
    var first=event.target.closest&&event.target.closest('.dp-upload-preview__delete');
    if(first&&state){state.extraUrls.splice(0,state.extraUrls.length);var gallery=document.querySelector('.fadeless-extra-gallery');if(gallery)gallery.innerHTML=''}
  },true);
  var style=document.createElement('style');style.id='fadeless-delete-buttons-v1';
  style.textContent=
    '.fadeless-horizontal-photo-preview>.dp-upload-preview__thumb--large{overflow:visible!important}'+
    '.fadeless-horizontal-photo-preview .dp-upload-preview__thumb-inner{overflow:hidden!important}'+
    '.fadeless-horizontal-photo-preview .dp-upload-preview__delete,.fadeless-horizontal-photo-preview .fadeless-extra-delete{display:flex!important;position:absolute!important;top:3px!important;right:3px!important;z-index:5!important;width:22px!important;height:22px!important;padding:0!important;border:1px solid rgba(64,57,52,.35)!important;border-radius:50%!important;align-items:center!important;justify-content:center!important;background:#fff!important;color:#403934!important;font:600 18px/1 Arial,sans-serif!important;box-shadow:0 1px 4px rgba(0,0,0,.18)!important;cursor:pointer!important}'+
    '.fadeless-horizontal-photo-preview .dp-upload-preview__delete svg{display:none!important}'+
    '.fadeless-horizontal-photo-preview .dp-upload-preview__delete:before{content:"×"!important}'+
    '.fadeless-horizontal-photo-preview .fadeless-extra-photo{overflow:hidden!important}'+
    '.fadeless-horizontal-photo-preview .fadeless-extra-delete:hover,.fadeless-horizontal-photo-preview .dp-upload-preview__delete:hover{background:#403934!important;color:#fff!important}';
  document.head.appendChild(style);
})();

/* FADELESS ART STYLE LABELS V1 */
(function(){
  if(window.__fadelessArtStyleLabelsV1)return;window.__fadelessArtStyleLabelsV1=true;
  function relabel(){document.querySelectorAll('button,a,span,div').forEach(function(el){if(el.children.length===0&&el.textContent.trim()==='Artist')el.textContent='Art Style'});}
  document.addEventListener('DOMContentLoaded',relabel);window.addEventListener('pageshow',relabel);new MutationObserver(relabel).observe(document.documentElement,{childList:true,subtree:true});relabel();
})();
